<template>
  <div class="logwork-timeline page-wrapper">
    <HeaderControls
      :title="$t('logwork.timeline.title')"
      :dateFormat="dateFormat"
      :viewOptions="viewOptions"
      :getPopupContainer="getPopupContainer"
      @on-logwork="onLogwork"
      @open-submit="openSubmitModal"
      @export-excel="exportExcel"
      @date-change="handleDateChange"
      @view-change="handleViewChange"
      :dateRange.sync="dateRange"
      :viewMode.sync="viewMode"
    />

    <FilterSection
      :scope.sync="scope"
      :scopeOptions="scopeOptions"
      :selectedProjects.sync="selectedProjects"
      :projectOptions="projectOptions"
      :selectedMembers.sync="selectedMembers"
      :memberOptions="memberOptions"
      :groupBy.sync="groupBy"
      :groupByOptions="groupByOptions"
      :visibleGroupByOptions="visibleGroupByOptions"
      @scope-change="handleScopeChange"
      @projects-change="debouncedFetchLogwork"
      @members-change="debouncedFetchLogwork"
      @groupby-change="debouncedFetchLogwork"
    />

    <LogworkTable
      ref="logworkTable"
      :loading="loading"
      :issueHeader="
        scope === 'Project'
          ? $t('logwork.table.project')
          : $t('logwork.table.member')
      "
      :sumHeader="$t('logwork.table.sum')"
      :dayColumns="dayColumns"
      :monthHeaders="monthHeaders"
      :weekHeaders="weekHeaders"
      :displayedRows="displayedRows"
      :viewMode="viewMode"
      :itemType="currentItemType"
      :view="viewMode"
      :fromDate="dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : null"
      :toDate="dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : null"
      :groupBy="scope === 'Project' ? 'project' : 'member'"
      :memberIds="selectedMembers"
      :projectIds="selectedProjects"
      :expandableRows="true"
      :expandedRows="expandedRows"
      :isShowHour="isShowHour"
      :expandTrigger="expandTrigger"
      @toggle-expansion="handleToggleExpansion"
      @view-detail="handleViewDetail"
      @create-logwork="handleCreateLogwork"
    />

    <div v-if="timelineHasMore" class="load-more-container">
      <a-button
        type="default"
        :loading="loadingMoreTimeline"
        @click="loadMoreTimeline"
      >
        {{ $t("commons.load_more") || "Load more" }}
      </a-button>
    </div>

    <!-- Logwork Submit Modal -->
    <LogworkSubmitModal
      :visible.sync="submitModalVisible"
      @success="handleSubmitSuccess"
    />

    <!-- Logwork Entry Modal -->
    <LogworkModal
      :visible.sync="logworkModalVisible"
      :logworkId="logworkId"
      :defaultDate="logworkDefaultDate"
      :defaultProjectId="logworkDefaultProjectId"
      :employeeId="logworkEmployeeId"
      @success="handleLogworkSuccess"
    />
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import LogworkService from "../../api/logwork";
import HeaderControls from "../../components/logwork/HeaderControls.vue";
import FilterSection from "../../components/logwork/FilterSection.vue";
import LogworkTable from "../../components/logwork/LogworkTable.vue";
import LogworkSubmitModal from "./LogworkSubmitModal.vue";
import LogworkModal from "./LogworkModal.vue";

const dateFormat = "DD/MM/YYYY";

const service = LogworkService;

export default {
  name: "LogworkTimeline",
  components: {
    HeaderControls,
    FilterSection,
    LogworkTable,
    LogworkSubmitModal,
    LogworkModal
  },
  data() {
    const end = moment();
    const start = moment(end).subtract(1, 'month');
    return {
      logworkDefaultProjectId: null,
      dateFormat,
      scopeOptions: [
        { label: this.$t("logwork.scope.project"), value: "Project" },
        { label: this.$t("logwork.scope.member"), value: "Member" }
      ],
      groupByOptions: [
        { label: this.$t("logwork.group.summary"), value: "By Summary" },
        { label: this.$t("logwork.group.member"), value: "By Member" },
        { label: this.$t("logwork.group.project"), value: "By Project" },
        { label: this.$t("logwork.group.issue"), value: "By Issue" }
      ],
      // visibleGroupByOptions filters options based on current scope (e.g. hide "By Member" when scope is Member)

      viewOptions: [
        { label: this.$t("logwork.view.day"), value: "day" },
        { label: this.$t("logwork.view.week"), value: "week" },
        { label: this.$t("logwork.view.month"), value: "month" }
      ],
      scope: "Project",
      selectedProjects: [],
      selectedMembers: [],
      groupBy: "By Member",
      viewMode: "day",
      dateRange: [start, end],
      projects: [],
      members: [],
      timelineData: [],
      logEntries: [],
      loading: false,
      error: null,
      submitModalVisible: false,
      logworkModalVisible: false,
      logworkId: null,
      logworkDefaultDate: null,
      logworkEmployeeId: null,
      expandedRows: [],

      // Pagination for timeline
      timelinePage: 1,
      timelinePerPage: 50,
      timelineHasMore: true,
      loadingMoreTimeline: false,

      // Debounce and cancellation helpers
      debounceDelay: 200, // ms
      fetchLogworkTimer: null,
      currentRequestCancelSource: null,
      isShowHour: true,
      expandTrigger: 0
    };
  },
  async created() {
    // Ensure projects (and members when scope is Member) are loaded before initial timeline fetch
    await this.fetchProjects();
    if (this.scope === "Member") {
      await this.fetchMembers();
    }
    // Use debounced fetch after dependencies are ready
    this.debouncedFetchLogwork();
  },
  mounted() {
    // Add class to main so padding reset applies only for this view
    const main = document.querySelector("main.c-main");
    if (main) main.classList.add("logwork-main");
  },
  beforeDestroy() {
    const main = document.querySelector("main.c-main");
    if (main) main.classList.remove("logwork-main");
  },
  computed: {
    projectOptions() {
      return this.projects.map(p => ({ label: p.name, value: p.id }));
    },

    memberOptions() {
      if (!this.members || !Array.isArray(this.members)) {
        return [];
      }
      return this.members
        .filter(m => {
          if (this.scope === "Member") return true;
          return (
            this.selectedProjects &&
            this.selectedProjects.includes(m.project_id)
          );
        })
        .map(m => ({ label: m.employee_name || m.name, value: m.id }));
    },

    filteredEntries() {
      let entries = this.logEntries || [];

      if (this.scope === "Project" && this.selectedProjects) {
        entries = entries.filter(e =>
          this.selectedProjects.includes(e.projectId)
        );
      } else if (this.scope === "Member") {
        // When in Member scope, if no members are selected, show no entries
        if (!this.selectedMembers || this.selectedMembers.length === 0) {
          entries = [];
        } else {
          // Normalize both sides to String for comparison
          const selectedMemberStrs = this.selectedMembers.map(String);
          entries = entries.filter(e =>
            selectedMemberStrs.includes(String(e.memberId))
          );
        }
      }

      if (this.groupBy === "By Summary") return entries;

      // Skip date filtering for week and month views since dates are in aggregated format (e.g., "2023-10_41" for week, "2023-10" for month)
      if (this.viewMode === "week" || this.viewMode === "month") return entries;

      if (!this.dateRange || this.dateRange.length < 2) return entries;

      const [start, end] = this.dateRange;
      return entries.filter(e => {
        const d = moment(e.date);
        return d.isBetween(start, end, "day", "[]");
      });
    },

    dayColumns() {
      const [start, end] = this.dateRange;
      if (this.viewMode === "month")
        return this.buildMonthViewColumns(start, end);
      if (this.viewMode === "day") return this.buildDayViewColumns();
      const result = this.buildWeekViewColumns(start, end);
      return result;
    },

    monthHeaders() {
      if (this.viewMode === "day") {
        const groups = [];
        this.dayColumns.forEach(col => {
          const weekLabel = col.weekLabel;
          const label = `${col.monthLabel} (${weekLabel})`;
          const existing = groups.find(g => g.label === label);
          if (existing) existing.colspan += 1;
          else groups.push({ label: label, colspan: 1 });
        });
        return groups;
      }

      if (this.viewMode === "week") {
        const groups = [];
        this.dayColumns.forEach(col => {
          const monthLabel = col.monthLabel;
          const existing = groups.find(g => g.label === monthLabel);
          if (existing) existing.colspan += 1;
          else groups.push({ label: monthLabel, colspan: 1 });
        });
        return groups;
      }

      if (this.viewMode === "month") {
        const groups = [];
        this.dayColumns.forEach(col => {
          const year = col.year;
          const existing = groups.find(g => g.label === year);
          if (existing) existing.colspan += 1;
          else groups.push({ label: year, colspan: 1 });
        });
        return groups;
      }

      return [];
    },

    weekHeaders() {
      if (this.viewMode === "day") {
        return this.dayColumns.map(col => ({
          label: col.dayName || "",
          colspan: 1
        }));
      }

      if (this.viewMode === "week") {
        const groups = [];
        this.dayColumns.forEach(col => {
          const weekLabel = `Tuần ${col.weekLabel.substring(1)}`;
          const existing = groups.find(g => g.label === weekLabel);
          if (existing) existing.colspan += 1;
          else groups.push({ label: weekLabel, colspan: 1 });
        });
        return groups;
      }

      if (this.viewMode === "month") {
        const groups = [];
        this.dayColumns.forEach(col => {
          const quarterLabel = col.weekLabel;
          const existing = groups.find(g => g.label === quarterLabel);
          if (existing) existing.colspan += 1;
          else groups.push({ label: quarterLabel, colspan: 1 });
        });
        return groups;
      }

      return [];
    },

    displayedRows() {
      if (this.groupBy === "By Summary") return this.buildSummaryRows();
      if (this.groupBy === "By Member" || this.groupBy === "By Project") return this.buildMemberRows();
      if (this.groupBy === "By Issue") return this.buildIssueRows();
      return [];
    },

    // When scope is Member, hide the "By Member" option and use "By Summary" by default
    visibleGroupByOptions() {
      if (this.scope === "Member") {
        // For Member scope: show Summary, Project, Issue
        return this.groupByOptions.filter(o => o.value !== "By Member");
      }
      if (this.scope === "Project") {
        // For Project scope: show Summary, Member, Issue
        return this.groupByOptions.filter(o => o.value !== "By Project");
      }
      return this.groupByOptions;
    },

    currentItemType() {
      return this.groupBy
        .replace("By ", "")
        .toLowerCase()
        .replace("issue", "issues");
    }
  },

  watch: {
    selectedProjects(newVal, oldVal) {
      const oldIds = (oldVal || []).map(Number);
      const newIds = (newVal || []).map(Number);
      const changed =
        newIds.length !== oldIds.length ||
        newIds.some(id => !oldIds.includes(id)) ||
        oldIds.some(id => !newIds.includes(id));
      if (changed) {
        // Safe check for mounted component and table ref
        if (this.$refs.logworkTable && !this.$refs.logworkTable._isDestroyed) {
          this.$refs.logworkTable.clearState?.();
        }
        this.expandedRows = [];
        this.resetTimelinePagination();
        this.debouncedFetchLogwork();
      }
    },
    selectedMembers(newVal, oldVal) {
      const oldIds = (oldVal || []).map(Number);
      const newIds = (newVal || []).map(Number);
      const changed =
        newIds.length !== oldIds.length ||
        newIds.some(id => !oldIds.includes(id)) ||
        oldIds.some(id => !newIds.includes(id));
      if (changed) {
        // Safe check for mounted component and table ref
        if (this.$refs.logworkTable && !this.$refs.logworkTable._isDestroyed) {
          this.$refs.logworkTable.clearState?.();
        }
        this.expandedRows = [];
        this.resetTimelinePagination();
        this.debouncedFetchLogwork();
      }
    },
    groupBy(newVal, oldVal) {
      if (newVal !== oldVal) {
        // Safe check for mounted component and table ref
        if (this.$refs.logworkTable && !this.$refs.logworkTable._isDestroyed) {
          this.$refs.logworkTable.clearState?.();
        }
        this.expandedRows = [];
        this.resetTimelinePagination();
        this.debouncedFetchLogwork();
      }
    }
  },

  methods: {
    resetTimelinePagination() {
      this.timelinePage = 1;
      this.timelineHasMore = true;
      this.loadingMoreTimeline = false;
      this.timelineData = [];
      this.logEntries = [];
    },

    /**
     * Fetch projects from API
     */
    fetchProjects() {
      this.loading = true;
      return service
        .getProjects()
        .then(response => {
          this.projects = response.data?.data || [];
          // Auto-select running projects (watcher will trigger fetch)
          if (this.scope === "Project" && this.selectedProjects.length === 0) {
            this.selectedProjects = this.projects.map(p => p.id);
          }
          this.error = null;
        })
        .catch(error => {
          console.error("Failed to fetch projects:", error);
          this.error = "Failed to load projects";
          this.$message.error(this.error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    /**
     * Fetch members from API
     */
    fetchMembers() {
      const params = {};
      if (
        this.selectedProjects &&
        Array.isArray(this.selectedProjects) &&
        this.selectedProjects.length > 0
      ) {
        params.project_ids = this.selectedProjects.join(",");
      }
      return service
        .getMembers(params)
        .then(response => {
          this.members = response.data?.data || [];
          // Auto-select all members (watcher will trigger fetch) - normalize to String
          if (this.scope === "Member" && this.selectedMembers.length === 0) {
            this.selectedMembers = this.members.map(m => String(m.id));
          }
          this.error = null;
        })
        .catch(error => {
          console.error("Failed to fetch members:", error);
          this.error = "Failed to load members";
        });
    },

    /**
     * Fetch logwork data from API
     */
    debouncedFetchLogwork() {
      if (this.scope === "Project" && this.groupBy === "By Member") {
        this.isShowHour = false;
      }
      else {
        this.isShowHour = true;
      }
      // Clear existing debounce timer
      if (this.fetchLogworkTimer) {
        clearTimeout(this.fetchLogworkTimer);
      }
      this.fetchLogworkTimer = setTimeout(() => {
        this.fetchLogworkTimer = null;
        this.performFetchLogwork();
      }, this.debounceDelay);
    },

    loadMoreTimeline() {
      if (this.loadingMoreTimeline || !this.timelineHasMore) return;
      this.loadingMoreTimeline = true;
      this.performFetchLogwork(true).finally(() => {
        this.loadingMoreTimeline = false;
      });
    },

    performFetchLogwork(append = false) {
      // Avoid making a request before dependent lists are loaded.
      // If scope is Project but projects haven't been loaded yet, bail —
      // fetchProjects() will trigger debouncedFetchLogwork() when it finishes.
      if (
        this.scope === "Project" &&
        (!this.projects || this.projects.length === 0)
      ) {
        return;
      }
      // Similarly for Member scope: wait until members are loaded
      if (
        this.scope === "Member" &&
        (!this.members || this.members.length === 0)
      ) {
        return;
      }

      if (append && !this.timelineHasMore) return;

      // Cancel previous in-flight request if any
      if (this.currentRequestCancelSource) {
        this.currentRequestCancelSource.cancel("Cancelled by new request");
        this.currentRequestCancelSource = null;
      }

      const source = axios.CancelToken.source();
      this.currentRequestCancelSource = source;

      this.loading = !append;
      const [start, end] = this.dateRange;
      const nextPage = append ? this.timelinePage + 1 : 1;

      const params = {
        view: this.viewMode,
        from_date: start.format("YYYY-MM-DD"),
        to_date: end.format("YYYY-MM-DD"),
        // group_by: groupByParam,
        item_type: this.groupBy
          .replace("By ", "")
          .toLowerCase()
          .replace("issue", "issues"),
        page: nextPage,
        per_page: this.timelinePerPage
      };

      // Add filter based on scope
      if (this.scope === "Project" && this.selectedProjects.length > 0) {
        params.project_ids = this.selectedProjects.join(",");
        params.group_by = 'project';
      } else if (this.scope === "Member" && this.selectedMembers.length > 0) {
        params.member_ids = this.selectedMembers.join(",");
        params.group_by = 'member';
      }

      return service
        .getTimeline(params, source.token)
        .then(response => {
          const newData = response.data?.data || [];
          const meta = response.data?.meta;
          const hasMore = meta
            ? meta.current_page < meta.last_page
            : newData.length === this.timelinePerPage;

          if (append) {
            this.timelineData = [...this.timelineData, ...newData];
            this.timelinePage = nextPage;
          } else {
            this.timelineData = newData;
            this.timelinePage = 1;
            this.expandedRows = []; // Clear expanded rows when loading new data
          }
          this.timelineHasMore = hasMore;
          // Process API response and build logEntries for display
          this.processTimelineData(this.timelineData);
          this.error = null;
          this.expandTrigger++;
        })
        .catch(error => {
          // Ignore cancellations initiated by us
          if (axios.isCancel && axios.isCancel(error)) {
            // console.debug("Timeline request cancelled");
            return;
          }

          console.error("Failed to fetch logwork:", error);
          this.error = "Failed to load logwork data";
          this.$message.error(this.error);
          // Fallback: show empty data
          this.logEntries = [];
          this.timelineHasMore = false;
        })
        .finally(() => {
          this.loading = false;
          this.loadingMoreTimeline = false;
          this.currentRequestCancelSource = null;
        });
    },

    /**
     * Process API timeline data into displayable logEntries
     */
    processTimelineData(data) {
      try {
        this.logEntries = data.flatMap(item => {
          const entries = [];

          // Ensure item.summary exists and is an object
          if (item.summary && typeof item.summary === "object") {
            // Convert to entries and process
            const summaryEntries = Object.entries(item.summary);

            summaryEntries.forEach(([date, summaryData]) => {
              // summaryData is now {hours: number, id: number}
              const numericHours = typeof summaryData === "object" && summaryData.hours
                ? parseFloat(summaryData.hours)
                : typeof summaryData === "string"
                ? parseFloat(summaryData)
                : summaryData;

              const projectRaw =
                item.project_id || item.projectId || item.project?.id || item.id;
              const memberRaw = item.member_id || item.memberId || item.employee_id || item.id;

              const projectId =
                projectRaw !== undefined && projectRaw !== null
                  ? Number.isNaN(Number(projectRaw))
                    ? projectRaw
                    : Number(projectRaw)
                  : null;
              const memberId =
                memberRaw !== undefined && memberRaw !== null
                  ? String(memberRaw)
                  : null;

              const entry = {
                projectId,
                memberId,
                issueId: item.issue_id,
                issueTitle:
                  item.issue_title ||
                  item.issue_summary ||
                  item.title ||
                  item.name ||
                  "Unknown",
                date,
                hours: numericHours
              };
              entries.push(entry);
            });
          }

          return entries;
        });
      } catch (error) {
        console.error("Error in processTimelineData:", error);
        this.logEntries = [];
      }
    },
    async handleScopeChange(value) {
      if (value === "Project" && this.groupBy === "By Member") {
        this.isShowHour = false;
      }
      else {
        this.isShowHour = true;
      }
      // When switching scope, reset the appropriate filter
      if (value === "Project") {
        this.selectedMembers = [];
        // Fetch projects if not already loaded
        if (!this.projects || this.projects.length === 0) {
          await this.fetchProjects();
        }
        // Auto-select all projects
        this.selectedProjects = this.projects.map(p => p.id);
      } else if (value === "Member") {
        this.selectedProjects = [];
        // Ensure members are loaded (if dependent on selected projects)
        if (!this.members || this.members.length === 0) {
          await this.fetchMembers();
        }
        // When switching to Member scope, force groupBy to be "By Project" and auto-select members
        if (this.groupBy === "By Member" || this.groupBy === "By Summary") {
          this.groupBy = "By Project";
        }
        // Auto-select all members - normalize to String
        this.selectedMembers = this.members.map(m => String(m.id));
      }

      this.resetTimelinePagination();

      // Clear table state when scope changes
      if (this.$refs.logworkTable && this.$refs.logworkTable.clearState) {
        this.$refs.logworkTable.clearState();
      }
      this.debouncedFetchLogwork();
    },

    handleDateChange() {
      if (this.$refs.logworkTable && this.$refs.logworkTable.clearState) {
        this.$refs.logworkTable.clearState();
      }
      // Reset parent expansion state as well
      this.expandedRows = [];
      this.resetTimelinePagination();
      this.debouncedFetchLogwork();
    },

    handleViewChange() {
      if (this.$refs.logworkTable && this.$refs.logworkTable.clearState) {
        this.$refs.logworkTable.clearState();
      }
      this.resetTimelinePagination();
      this.debouncedFetchLogwork();
    },

    handleViewDetail(id) {
      this.logworkId = id;
      this.logworkModalVisible = true;
    },

    handleCreateLogwork(payload) {
      this.logworkId = null;
      this.logworkDefaultDate = payload?.date || null;
      this.logworkEmployeeId = payload?.employee_id || null;
      this.logworkDefaultProjectId = payload?.project_id || null;
      this.logworkModalVisible = true;
    },

    getPopupContainer(trigger) {
      return trigger.parentNode;
    },

    groupValues(entries) {
      const values = {};
      this.dayColumns.forEach(d => {
        values[d.date] = 0;
      });

      if (this.viewMode === "month") {
        return this.groupValuesForMonthView(entries, values);
      } else if (this.viewMode === "day") {
        return this.groupValuesForDayView(entries, values);
      } else {
        return this.groupValuesForWeekView(entries, values);
      }
    },

    groupValuesForMonthView(entries, values) {
      entries.forEach(e => {
        const matchingCol = this.dayColumns.find(col => col.date === e.date);
        if (matchingCol) {
          values[matchingCol.date] = (values[matchingCol.date] || 0) + e.hours;
        }
      });
      return values;
    },

    groupValuesForDayView(entries, values) {
      entries.forEach(e => {
        const matchingCol = this.dayColumns.find(col => col.date === e.date);
        if (matchingCol) {
          values[matchingCol.date] = (values[matchingCol.date] || 0) + e.hours;
        }
      });
      return values;
    },

    groupValuesForWeekView(entries, values) {
      entries.forEach(e => {
        const parts = e.date.split('_');
        let weekNum;
        if (parts.length === 2) {
          weekNum = parseInt(parts[1]);
        }
        const matchingCol = this.dayColumns.find(col => col.weekNum === weekNum);
        if (matchingCol) {
          values[matchingCol.date] = (values[matchingCol.date] || 0) + e.hours;
        }
      });
      return values;
    },

    getMergedProjectList() {
      const map = new Map();
      if (this.projects && Array.isArray(this.projects)) {
        this.projects.forEach(p =>
          map.set(Number(p.id), { id: Number(p.id), name: p.name })
        );
      }
      if (this.timelineData && Array.isArray(this.timelineData)) {
        this.timelineData.forEach(p => {
          const id = Number(p.id);
          if (!map.has(id)) map.set(id, { id, name: p.name });
        });
      }
      return Array.from(map.values());
    },

    getSelectedProjectIds(projectList) {
      const availableIds = projectList.map(p => p.id);

      // If scope is Member, derive project IDs from timelineData or use all available
      if (this.scope === "Member") {
        if (!this.selectedMembers || this.selectedMembers.length === 0) {
          return [];
        }
        // First try to collect project ids from selected members
        const memberProjectIds = new Set();
        const selectedMemberStrs = this.selectedMembers.map(String);
        (this.members || []).forEach(m => {
          if (selectedMemberStrs.includes(String(m.id))) {
            const pid = Number(m.project_id || m.projectId);
            if (!Number.isNaN(pid) && pid !== 0) memberProjectIds.add(pid);
          }
        });

        // If members don't have project_id, derive from timelineData
        if (memberProjectIds.size === 0 && this.timelineData && this.timelineData.length > 0) {
          this.timelineData.forEach(item => {
            const pid = Number(item.project_id || item.projectId || item.id);
            if (!Number.isNaN(pid)) memberProjectIds.add(pid);
          });
        }

        // If still empty, use all available projects
        if (memberProjectIds.size === 0) {
          return availableIds;
        }

        const derived = Array.from(memberProjectIds).filter(id =>
          availableIds.includes(id)
        );
        return derived.length > 0 ? derived : availableIds;
      }

      // Default (Project scope): use selectedProjects if provided, else all available
      let selectedIds =
        this.selectedProjects && this.selectedProjects.length > 0
          ? this.selectedProjects.map(id => Number(id))
          : availableIds;

      if (!selectedIds.some(id => availableIds.includes(id))) {
        selectedIds = availableIds;
      }
      return selectedIds;
    },

    buildSummaryRows() {
      const projectList = this.getMergedProjectList();
      if (!projectList || projectList.length === 0) return [];

      const selectedIds = this.getSelectedProjectIds(projectList);

      const rows = projectList
        .filter(p => selectedIds.includes(p.id))
        .map(project => {
          const entries = this.filteredEntries.filter(
            e => e.projectId === project.id
          );

          const values = this.groupValues(entries);

          return {
            id: project.id,
            label: project.name,
            total: this.sumHours(entries),
            values: values,
            level: 0,
            isGroup: false
          };
        });

      return rows;
    },
    buildMemberRows() {
      const rows = [];
      const selectedMemberStrs = this.selectedMembers.map(String);

      // For Member scope: render members directly if they don't have project_id
      if (this.scope === "Member") {
        // Check if members have project_id
        const membersHaveProjectId = this.members.some(m => m.project_id || m.projectId);

        if (!membersHaveProjectId) {
          // Render members directly without project grouping
          this.members
            .filter(m => selectedMemberStrs.includes(String(m.id)))
            .forEach(member => {
              const memberEntries = this.filteredEntries.filter(
                e => String(e.memberId) === String(member.id)
              );
              rows.push({
                id: member.id,
                label: member.employee_name || member.name,
                total: this.sumHours(memberEntries),
                values: this.groupValues(memberEntries),
                level: 0,
                isGroup: false,
                employee_id: member.employee_id
              });
            });
          return rows;
        }
      }

      // Default: group by project then show members
      const projectList = this.getMergedProjectList();
      if (!projectList || projectList.length === 0) return [];

      const selectedIds = this.getSelectedProjectIds(projectList);

      projectList
        .filter(p => selectedIds.includes(p.id))
        .forEach(project => {
          const projectEntries = this.filteredEntries.filter(
            e => e.projectId === project.id
          );

          rows.push({
            id: `${project.id}`,
            label: project.name,
            total: this.sumHours(projectEntries),
            values: this.groupValues(projectEntries),
            level: 0,
            isGroup: true
          });

          this.members
            .filter(m => {
              const memberProjectId = Number(m.project_id || m.projectId);
              return (
                memberProjectId === Number(project.id) &&
                selectedMemberStrs.includes(String(m.id))
              );
            })
            .forEach(member => {
              const memberEntries = projectEntries.filter(
                e => String(e.memberId) === String(member.id)
              );
              rows.push({
                id: member.id,
                label: member.employee_name || member.name,
                total: this.sumHours(memberEntries),
                values: this.groupValues(memberEntries),
                level: 1,
                isGroup: false
              });
            });
        });
      return rows;
    },
    buildIssueRows() {
      const rows = [];

      // Khi scope là Member, hiển thị theo Member -> Issue (không theo Project)
      if (this.scope === "Member") {
        const selectedMemberStrs = this.selectedMembers.map(String);

        this.members
          .filter(m => selectedMemberStrs.includes(String(m.id)))
          .forEach(member => {
            const memberEntries = this.filteredEntries.filter(
              e => String(e.memberId) === String(member.id)
            );

            // Dòng nhóm theo member
            rows.push({
              id: member.id,
              label: member.employee_name || member.name,
              total: this.sumHours(memberEntries),
              values: this.groupValues(memberEntries),
              level: 0,
              isGroup: true,
              employee_id: member.employee_id
            });

            // Các dòng issue thuộc member đó
            const issues = this.uniqueIssues(memberEntries);
            issues.forEach(issue => {
              const issueEntries = memberEntries.filter(
                e => e.issueId === issue.id
              );
              rows.push({
                id: `issue-${issue.id}`,
                label: issue.title,
                meta: issue.id,
                total: this.sumHours(issueEntries),
                values: this.groupValues(issueEntries),
                level: 1,
                isGroup: false
              });
            });
          });

        return rows;
      }

      if (this.scope === "Project") {
        const projectList = this.getMergedProjectList();
        if (!projectList || projectList.length === 0) {
          return [];
        }

        const selectedIds = this.getSelectedProjectIds(projectList);

        projectList
          .filter(p => selectedIds.includes(p.id))
          .forEach(project => {
            const projectEntries = this.filteredEntries.filter(
              e => e.projectId === project.id
            );

            rows.push({
              id: project.id,
              label: project.name,
              total: this.sumHours(projectEntries),
              values: this.groupValues(projectEntries),
              level: 0,
              isGroup: true
            });

            const issues = this.uniqueIssues(projectEntries);
            issues.forEach(issue => {
              const issueEntries = projectEntries.filter(
                e => e.issueId === issue.id
              );
              rows.push({
                id: `issue-${issue.id}`,
                label: issue.title,
                meta: issue.id,
                total: this.sumHours(issueEntries),
                values: this.groupValues(issueEntries),
                level: 1,
                isGroup: false
              });
            });
          });
        return rows;
      }
    },
    uniqueIssues(entries) {
      const map = {};
      entries.forEach(e => {
        if (e.issueId && !map[e.issueId]) {
          map[e.issueId] = { id: e.issueId, title: e.issueTitle };
        }
      });
      return Object.values(map);
    },
    sumHours(entries) {
      return entries.reduce((acc, cur) => acc + cur.hours, 0);
    },
    formatHour(value) {
      if (!value) return 0;
      return Number(value.toFixed(1))
        .toString()
        .replace(/\.0$/, "");
    },
    barStyle(hours) {
      const width = Math.min(100, (hours / 10) * 100);
      return {
        width: `${width}%`
      };
    },
    rowRowClass(row) {
      return {
        "group-row": row.isGroup,
        "level-1": row.level === 1
      };
    },

    /* Helper: build columns for views */
    // Week view: Show weeks with date range labels
    buildWeekViewColumns(start, end) {
      const weeks = [];
      const current = start.clone().startOf("isoWeek");
      const endDate = end.clone().endOf("isoWeek");

      while (current.isSameOrBefore(endDate, "day")) {
        const weekStart = current.clone();
        const weekEnd = current.clone().endOf("isoWeek");
        const weekNum = current.isoWeek();
        const year = current.format("YYYY");
        const month = current.format("MM");
        const dateRange = `${weekStart.format("D")}-${weekEnd.format("D")}`;

        weeks.push({
          date: `${year}-${month}_${weekNum.toString().padStart(2, '0')}`,
          dayName: dateRange,
          dayNumber: current.date(),
          weekLabel: `T${weekNum}`,
          monthLabel: `Tháng ${current.format("M/YYYY")}`,
          isWeekend: false,
          weekStart: weekStart.format("YYYY-MM-DD"),
          weekEnd: weekEnd.format("YYYY-MM-DD"),
          dateRange: dateRange,
          weekNum: weekNum
        });
        current.add(1, "week");
      }
      return weeks;
    },

    // Day view: Show all days with week labels and day names
    buildDayViewColumns() {
      const [start, end] = this.dateRange;
      const days = [];
      const current = start.clone();
      const dayMap = {
        0: "CN",
        1: "Hai",
        2: "Ba",
        3: "Bốn",
        4: "Năm",
        5: "Sáu",
        6: "Bảy"
      };

      while (current.isSameOrBefore(end, "day")) {
        const dayAbbr = current.format("dd"); // Mo, Tu, We, etc.
        const dayOfWeek = current.day(); // 0 (Sunday) to 6 (Saturday)

        days.push({
          date: current.format("YYYY-MM-DD"),
          dayName: dayMap[dayOfWeek],
          dayNumber: current.date(),
          weekLabel: `T${current.isoWeek()}`,
          monthLabel: `Tháng ${current.format("M/YYYY")}`,
          dayAbbr: dayAbbr,
          isWeekend: [0, 6].includes(dayOfWeek) // 0 is Sunday, 6 is Saturday
        });
        current.add(1, "day");
      }
      return days;
    },

    // Month view: Show all weeks in date range with year/month grouping
    buildMonthViewColumns(start, end) {
      const months = [];
      const current = start.clone().startOf("month");
      const endDate = end.clone().endOf("month");

      while (current.isSameOrBefore(endDate, "month")) {
        const year = current.format("YYYY");
        const month = current.format("MM");
        const quarter = Math.ceil(parseInt(month) / 3);

        months.push({
          date: `${year}-${month}-01`,
          dayName: month, // Show month number in row 3
          dayNumber: 0,
          weekLabel: `${year}-Q${quarter}`,
          monthLabel: month,
          year: year,
          isWeekend: false,
          weekStart: current.format("YYYY-MM-DD"),
          weekEnd: current.endOf("month").format("YYYY-MM-DD"),
          weekNum: quarter // Use quarter as weekNum for consistency
        });
        current.add(1, "month");
      }
      return months;
    },

    exportExcel() {
      const [start, end] = this.dateRange;
      const params = {
        from_date: start.format("YYYY-MM-DD"),
        to_date: end.format("YYYY-MM-DD")
      };

      this.$message.loading("Exporting...", 0);
      service
        .exportLogwork(params)
        .then(response => {
          // Create a blob URL and download the file
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `logwork_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.$message.success(this.$t("logwork.actions.export_success"));
        })
        .catch(error => {
          console.error("Export failed:", error);
          this.$message.error(this.$t("logwork.actions.export_error"));
        });
    },
    openSubmitModal() {
      this.submitModalVisible = true;
    },
    handleSubmitSuccess() {
      // Refresh logwork data after successful submission
      this.debouncedFetchLogwork();
    },
    onLogwork() {
      this.logworkId = null;
      this.logworkDefaultDate = null;
      this.logworkEmployeeId = null;
      this.logworkModalVisible = true;
    },
    handleLogworkSuccess() {
      // Refresh logwork data after successful entry creation
      this.$refs.logworkTable?.clearState?.();
      this.debouncedFetchLogwork();
    },
    handleToggleExpansion({ rowId, expanded }) {
      if (expanded) {
        // Add to expanded rows if not already present
        if (!this.expandedRows.includes(rowId)) {
          this.expandedRows.push(rowId);
        }
        // Note: LogworkTable handles its own child data fetching
      } else {
        // Remove from expanded rows
        this.expandedRows = this.expandedRows.filter(id => id !== rowId);
      }
    }
  }
};
</script>

<style scoped lang="scss">
.logwork-timeline {
  padding: 16px;
}

.load-more-container {
  display: flex;
  justify-content: center;
  margin: 12px 0 8px;
}

@media (max-width: 1200px) {
  .logwork-timeline {
    .filter-section .filter-row {
      .filter-group {
        min-width: 160px;

        .project-select {
          min-width: 200px;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .logwork-timeline {
    .header-section {
      flex-direction: column;
      align-items: flex-start;

      .header-controls {
        width: 100%;
        flex-wrap: wrap;

        .date-picker {
          flex: 1;
          min-width: 200px;
        }
      }
    }

    .filter-section .filter-row {
      flex-direction: column;

      .filter-group {
        width: 100%;
      }
    }
  }
}
</style>
